/**
 * @lines98/client/lib/format-datetime — ТОЧНАЯ локализованная дата+время ДО СЕКУНД
 * (`formatDateTime(iso, lang)`) для строк Архива игр обоих альбомов («Свои»/«Чужие»).
 *
 * ЗАЧЕМ / WHY THIS EXISTS: owner перекрыл ОТНОСИТЕЛЬНУЮ дату («2 дня назад») в строках
 * Архива — теперь показываем ТОЧНУЮ дату+время сыгранной партии (`finished_at`) ДО
 * СЕКУНД, локализованную по языку игрока (игра 12-язычная). Это ОТДЕЛЬНЫЙ модуль рядом
 * с `lib/format.ts` (соседний ствол того же fallback-паттерна) — переиспользуется обоими
 * альбомами Архива; bespoke-инлайн в экране = REJECT (§модульность, owner-directive
 * 2026-05-30). Формат (порядок/разделители/12-24h) — UX-решение локали через
 * `Intl.DateTimeFormat`, НЕ хардкод строки (как `formatDateLabel`).
 *
 * SCOPE: одна чистая функция `formatDateTime(iso, lang?)`. Детерминизм — НЕ зависит от
 *   `Date.now()` (это ТОЧНАЯ дата, не относительная): один вход → один выход.
 * NOT IN SCOPE: относительная дата (`formatRelativeDate` в lib/format — соседний ствол);
 *   дата-только лейбл (`formatDateLabel`).
 *
 * Cross-references: тикет реплей-под-волна (frontend-lead 2026-06-24) AC-5/AC-6;
 *   образец lib/format.ts (тот же try/catch-fallback на битый ISO / падение Intl).
 */

import { formatDateLabel } from './format.js';

/**
 * Точная локализованная дата+ВРЕМЯ ДО СЕКУНД из ISO-8601 момента.
 *
 * Год+месяц+день + часы:минуты:СЕКУНДЫ через `Intl.DateTimeFormat(lang, {...})` —
 * секунды обязательны (owner verbatim «дата+время до секунд»). Локаль — переданный
 * `lang` (ru/en/… — НЕ хардкод). Fallback при битом ISO / падении Intl (битая локаль
 * → RangeError): не бросаем, не «Invalid Date», не пусто — отдаём `formatDateLabel`
 * (дата-только) либо сырой ISO, как `formatRelativeDate`.
 *
 * Детерминирован: НЕ относительный, не читает «сейчас» → один вход даёт один выход.
 *
 * @param iso  ISO-8601 строка (напр. `finished_at`).
 * @param lang BCP-47 локаль (по умолчанию ru — копи-язык по умолчанию).
 */
export function formatDateTime(iso: string, lang = 'ru'): string {
  try {
    const ms = Date.parse(iso);
    if (Number.isNaN(ms)) return formatDateLabel(iso);
    return new Intl.DateTimeFormat(lang, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(new Date(ms));
  } catch {
    // Битая локаль (RangeError) / падение Intl → дата-только лейбл (не пусто, не throw).
    return formatDateLabel(iso);
  }
}
